import { MouseEvent, RefObject, useRef, useState } from "react";
import useEvent from "react-use-event-hook";

export interface IUseCoursesCalendarCellExpandResult {
  expandAnchorRef: RefObject<HTMLSpanElement>;
  popperContentRef: RefObject<HTMLDivElement>;
  isExpandOpen: boolean;
  toggleExpandOpen: (event: MouseEvent<HTMLButtonElement>) => void;
  closeExpand: (event: MouseEvent | TouchEvent | Event) => void;
  forceCloseExpand: () => void;
}

export const useCoursesCalendarCellExpand =
  (): IUseCoursesCalendarCellExpandResult => {
    const expandAnchorRef = useRef<HTMLSpanElement>(null);
    const popperContentRef = useRef<HTMLDivElement>(null);
    const ignoreClickAwayRef = useRef(false);
    const [isExpandOpen, setIsExpandOpen] = useState(false);

    const toggleExpandOpen = useEvent(
      (event: MouseEvent<HTMLButtonElement>): void => {
        event.stopPropagation();
        setIsExpandOpen(prevOpen => {
          if (!prevOpen) {
            ignoreClickAwayRef.current = true;
            requestAnimationFrame(() => {
              ignoreClickAwayRef.current = false;
            });
          }

          return !prevOpen;
        });
      },
    );

    const forceCloseExpand = useEvent((): void => {
      setIsExpandOpen(false);
    });

    const closeExpand = useEvent(
      (event: MouseEvent | TouchEvent | Event): void => {
        if (ignoreClickAwayRef.current) {
          return;
        }

        const target = event.target;

        if (!(target instanceof Node)) {
          forceCloseExpand();
          return;
        }

        if (expandAnchorRef.current?.contains(target)) {
          return;
        }

        if (popperContentRef.current?.contains(target)) {
          return;
        }

        forceCloseExpand();
      },
    );

    return {
      expandAnchorRef,
      popperContentRef,
      isExpandOpen,
      toggleExpandOpen,
      closeExpand,
      forceCloseExpand,
    };
  };
